/**
 * @attr {string} leftDateOffset - left day shift from current moment (for start date)
 * @attr {string} rightDateOffset - right day shift from current moment (for end date)
 * @attr {Array} monthLabels -  labels for months
 * @attr {Array} weekDayLabels -  labels for week days
 * @attr {string} resetButtonLabel -  label for reset button
 * @attr {string} cancelButtonLabel -  label for cancel button
 * @attr {string} submitButtonLabel -  label for submit button
 *
 * <e2-datepicker-calendar
 *      leftDateOffset="0"
 *      monthLabels=["Jan", "Feb" ...]
 *      weekDayLabels=["M", "T" ...]
 *      resetButtonLabel="Reset date"
 *      >
 * </e2-datepicker-calendar>
 */

import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import moment from 'moment';
import { ifDefined } from 'lit-html/directives/if-defined';

import connect from '@elab/core/store/connect';
import { generateArrayOfConsecutiveNumbers } from '@elab/core/utils/DateUtils';
import { datePickerConstants, formFieldsConstants } from '@elab/core/utils/constants';
import { deferred } from '@elab/core/utils/webComponent';

import { FormElement } from '../Form';

import { getActions, getSelectors } from './redux';

const { DEFAULT_START_DATE, FORMAT_PANELS, DEFAULT_FORMAT } = datePickerConstants;

export class DatepickerCalendar extends FormElement {
    static get properties() {
        return {
            leftDateOffset: { type: String, attribute: 'left-date-offset' },
            rightDateOffset: { type: String, attribute: 'right-date-offset' },
            monthLabels: { type: Array, attribute: 'month-labels' },
            weekDayLabels: { type: Array, attribute: 'week-day-labels' },

            resetButtonLabel: { type: String, attribute: 'reset-button-label' },
            cancelButtonLabel: {
                type: String,
                attribute: 'cancel-button-label',
            },
            submitButtonLabel: {
                type: String,
                attribute: 'submit-button-label',
            },

            isOpen: { type: Boolean, attribute: false },
            isFocused: { type: Boolean, attribute: false },
            readonlyField: { type: Boolean, attribute: false },
            required: { type: Boolean, reflect: true },
            isCleanValue: { type: Boolean, attribute: 'is-clean-value' },
            isModal: { type: Boolean, attribute: 'is-modal' },
            day: { type: String, attribute: false },
            month: { type: String, attribute: false },
            year: { type: String, attribute: false },
            gtmLabel: {
                type: String,
                attribute: 'gtm-label',
            },
        };
    }

    constructor() {
        super();

        this.isOpen = false;
        this.isFocused = false;
        this.monthLabels = [];
        this.weekDayLabels = [];
        this.readonlyField = true;
        this.isCleanValue = false;
        this.isModal = false;
        this.day = '';
        this.month = '';
        this.year = '';
        this.selectOrder = 'MM-YYYY';
        this.gtmLabel = '';
    }

    firstUpdated() {
        this.startDate = this.getDateWithOffset(this.leftDateOffset, DEFAULT_START_DATE);
        this.startYear = this.startDate.year();
        this.endDate = this.getDateWithOffset(this.rightDateOffset, new Date());
        this.endYear = this.endDate.year();
        this.setDefaultDate();
    }

    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('isFocused')) {
            this.isOpen = this.isFocused;
        }
        if (changedProperties.has('leftDateOffset')) {
            this.startDate = this.getDateWithOffset(this.leftDateOffset, DEFAULT_START_DATE);
            this.startYear = this.startDate.year();
            this.setDefaultDate();
        }
        if (changedProperties.has('rightDateOffset')) {
            this.endDate = this.getDateWithOffset(this.rightDateOffset, new Date());
            this.endYear = this.endDate.year();
            this.setDefaultDate();
        }
    }

    getDateWithOffset(dateOffset, defaultDate) {
        if (dateOffset) {
            const shift = +dateOffset;
            return moment().add(shift, 'days');
        }

        return moment(defaultDate);
    }

    setOpenStatus(status) {
        const event = status ? 'FOCUS-FIELD' : 'BLUR-FIELD';
        this.dispatchEvent(new CustomEvent(event, { detail: { fieldName: this.fieldValue.name } }));
    }

    getMonthLabel(monthNumber) {
        return this.monthLabels[monthNumber - 1];
    }

    calculateYears() {
        return generateArrayOfConsecutiveNumbers(this.startYear, this.endYear);
    }

    calculateMonths(start = 1, end = 12) {
        return generateArrayOfConsecutiveNumbers(start, end);
    }

    getNumberOfDays() {
        return moment(
            `${this.year}-${this.month}`,
            `${FORMAT_PANELS.YEARS}-${FORMAT_PANELS.MONTHS}`,
        ).daysInMonth();
    }

    calculateDays() {
        if (!this.year || !this.month) {
            return;
        }
        const start = 1;
        const end = this.getNumberOfDays();

        return generateArrayOfConsecutiveNumbers(start, end);
    }

    get monthsList() {
        let startMonth = 1;
        let endMonth = 12;

        if (+this.year === this.startYear) {
            startMonth = this.startDate.month() + 1;
        }

        if (+this.year === this.endYear) {
            endMonth = this.endDate.month() + 1;
        }

        return this.calculateMonths(startMonth, endMonth) || [];
    }

    get daysList() {
        return this.calculateDays() || [];
    }

    get yearsList() {
        return this.calculateYears() || [];
    }

    get weekDayOffset() {
        const weekDay = moment(`${this.year}-${this.month}-01`, 'YYYY-MM-GG').day();

        if (weekDay === 0) {
            return weekDay;
        }
        return weekDay - 1;
    }

    clampDate() {
        this.selectDay(Math.min(this.getNumberOfDays(), this.day));
        const currentDate = moment(`${this.year}-${this.month}-${this.day}`, DEFAULT_FORMAT);

        if (currentDate.isBefore(this.startDate)) {
            this.selectYear(this.startDate.year());
            this.selectMonth(this.startDate.month());
            this.selectDay(this.startDate.date());
        } else if (currentDate.isAfter(this.endDate)) {
            this.selectYear(this.endDate.year());
            this.selectMonth(this.endDate.month());
            this.selectDay(this.endDate.date());
        }
    }

    onSelectValue({ detail }, currPanel) {
        const selectedValue = detail && parseInt(detail);

        switch (currPanel) {
            case FORMAT_PANELS.YEARS:
                this.year = selectedValue;
                break;
            case FORMAT_PANELS.MONTHS:
                this.month = selectedValue;
                break;
            default:
                return [];
        }

        this.clampDate();
    }

    getCurrentOptionsArray(panel) {
        switch (panel) {
            case FORMAT_PANELS.YEARS:
                return this.yearsList;
            case FORMAT_PANELS.MONTHS:
                return this.monthsList;
            default:
                return [];
        }
    }

    getSelectOptionsByPanel(panel) {
        const currentOptions = this.getCurrentOptionsArray(panel);

        return currentOptions.map(option => ({
            value: `${option}`.padStart(2, '0'),
            label: panel === FORMAT_PANELS.MONTHS ? this.getMonthLabel(option) : option,
        }));
    }

    getSelectDefaultValue(panel) {
        const defaultMonth = this.monthsList.includes(this.month) ? this.month : this.monthsList[0];

        switch (panel) {
            case FORMAT_PANELS.YEARS:
                return this.year;
            case FORMAT_PANELS.MONTHS:
                return `${defaultMonth}`.padStart(2, '0');
            default:
                return [];
        }
    }

    createSelect = panel => {
        return html`
            <e2-custom-select
                class="datepicker-calendar__select"
                css-modifier="datepicker"
                tabindex-value="-1"
                .options="${this.getSelectOptionsByPanel(panel)}"
                @select=${e => this.onSelectValue(e, panel)}
                default-value="${this.getSelectDefaultValue(panel)}"
                @focused=${() => this.handleFocusSwitch(true)}
                @blured=${() => this.handleFocusSwitch(false)}
                gtm-label="${ifDefined(this.gtmLabel || undefined)}"
            ></e2-custom-select>
        `;
    };

    selectedValue() {
        return html`
            <div
                class="datepicker-calendar__clean-value"
                @click="${() => this.setOpenStatus(true)}"
            >
                <v-slot name="default-label"></v-slot>
                ${this.fieldValue.value
                    ? html`
                          <div class="datepicker-calendar__value">${this.fieldValue.value}</div>
                      `
                    : ''}
                <v-slot name="icon"></v-slot>
            </div>
        `;
    }

    selectedValueField() {
        return html`
            <e2-input-field
                @click="${() => this.setOpenStatus(true)}"
                name="${this.fieldValue.name}"
                store-name="${this.storeName}"
                readonly="${this.readonlyField}"
                disabled
                hidden-focus-label
                show-disabled-as-normal
                gtm-label="${ifDefined(this.gtmLabel || undefined)}"
            >
                <div v-slot="error">
                    ${this.fieldValue.error}
                </div>
            </e2-input-field>
        `;
    }

    getFormattedDate(format) {
        const dateFormat = format || DEFAULT_FORMAT;
        return moment(`${this.year}-${this.month}-${this.day}`, DEFAULT_FORMAT).format(dateFormat);
    }

    selectDay(day) {
        this.day = day;
    }

    selectMonth(month) {
        this.month = month + 1;
    }

    selectYear(year) {
        this.year = year;
    }

    submit(event) {
        event.stopPropagation();
        const date = this.getFormattedDate(this.fieldValue.dateFormat);
        this.changeValue(date);
        this.setOpenStatus(false);
    }

    setDefaultDate() {
        const defaultDate =
            +this.leftDateOffset && !+this.rightDateOffset ? this.endDate : this.startDate;
        this.selectYear(defaultDate.year());
        this.selectMonth(defaultDate.month());
        this.selectDay(defaultDate.date());
    }

    cancel(event) {
        event.stopPropagation();
        this.setOpenStatus(false);

        if (!this.isModal) {
            this.setDefaultDate();
        }

        this.dispatchEvent(
            new CustomEvent('cancel', { detail: { fieldName: this.fieldValue.name } }),
        );
    }

    handleFocusSwitch = deferred((focused, isLabel = false) => {
        if (this.isModal && isLabel) {
            return;
        }
        this.setOpenStatus(focused);
    }, 100);

    handleClick = deferred(() => {
        this.dispatchEvent(
            new CustomEvent('FOCUS-FIELD', { detail: { fieldName: this.fieldValue.name } }),
        );
    }, 100);

    resetDateFieldValue(event) {
        event.stopPropagation();
        this.setDefaultDate();
        this.dispatchEvent(new CustomEvent('reset'));
    }

    innerContainer() {
        const datepickerModifiers = {
            'datepicker-calendar': true,
            'datepicker-calendar--opened': this.isOpen,
            'datepicker-calendar--modal': this.isModal,
        };

        return html`
            <label
                class="${classMap(datepickerModifiers)}"
                tabindex="0"
                @focus=${() => this.handleFocusSwitch(true, true)}
                @blur=${() => this.handleFocusSwitch(false, true)}
                @click=${this.handleClick}
            >
                ${this.isCleanValue ? this.selectedValue() : this.selectedValueField()}
                ${this.isModal
                    ? html`
                          <div class="datepicker-calendar__modal-background"></div>
                      `
                    : ''}
                <div class="datepicker-calendar__panel-wrapper">
                    <div class="datepicker-calendar__top-row">
                        ${this.selectOrder.split('-').map(this.createSelect)}
                        <div
                            class="datepicker-calendar__button"
                            @click="${this.resetDateFieldValue}"
                        >
                            ${this.resetButtonLabel}
                        </div>
                    </div>
                    ${this.dayPanel()}
                    <div class="datepicker-calendar__buttons">
                        <div
                            class="datepicker-calendar__button datepicker-calendar__button--cancel"
                            @click="${this.cancel}"
                        >
                            ${this.cancelButtonLabel}
                        </div>
                        <div
                            class="datepicker-calendar__button datepicker-calendar__button--submit"
                            @click="${this.submit}"
                        >
                            ${this.submitButtonLabel}
                        </div>
                    </div>
                </div>
            </label>
        `;
    }

    getDisabledDayFrom() {
        let startDay;
        let endDay;

        if (+this.year === this.startYear && this.month === this.startDate.month() + 1) {
            startDay = this.startDate.date();
        }

        if (+this.year === this.endYear && this.month === this.endDate.month() + 1) {
            endDay = this.endDate.date();
        }

        return { startDay, endDay };
    }

    getWeekDaysTemplate() {
        return html`
            <div class="datepicker-calendar__week">
                ${this.weekDayLabels.map(
                    weekLabel => html`
                        <div
                            class="datepicker-calendar__element datepicker-calendar__element--week"
                        >
                            ${weekLabel}
                        </div>
                    `,
                )}
            </div>
        `;
    }

    getDayTemplate = day => {
        const { startDay, endDay } = this.getDisabledDayFrom();
        const disabled = day < startDay || endDay < day;
        const dayClasses = classMap({
            'datepicker-calendar__element': true,
            'datepicker-calendar__element--selected': this.day === day,
            'datepicker-calendar__element--disabled': disabled,
        });

        return html`
            <div class="${dayClasses}" @click="${() => !disabled && this.selectDay(day)}">
                ${day}
            </div>
        `;
    };

    dayPanel() {
        return html`
            <div class="datepicker-calendar__panel">
                ${this.getWeekDaysTemplate()}
                <div class="datepicker-calendar__body">
                    ${Array(this.weekDayOffset)
                        .fill()
                        .map(
                            () => html`
                                <div
                                    class="datepicker-calendar__element datepicker-calendar__element--empty"
                                ></div>
                            `,
                        )}
                    ${this.daysList.map(this.getDayTemplate)}
                </div>
            </div>
        `;
    }

    render() {
        if (!this.fieldValue) return html``;

        const hiddenField = this.fieldValue.type === formFieldsConstants.hidden;
        return html`
            <div ?hidden="${hiddenField}">
                <v-slot name="label"></v-slot>
                ${this.innerContainer()}
            </div>
        `;
    }
}

const mapStateToProps = (state, ctx) => {
    const selectors = getSelectors(ctx.storeName);
    return {
        required: selectors.isDataFieldRequired(state, ctx.name),
        isFocused: selectors.isDataFieldFocused(state, ctx.name),
    };
};

const mapDispatchToEvents = (dispatch, ctx) => {
    const actions = getActions(ctx.storeName);
    return {
        cancel: ({ detail: { fieldName } }) => dispatch(actions.cancel(fieldName)),
        reset: () => dispatch(actions.reset()),
    };
};

export default () =>
    customElements.define(
        'e2-datepicker-calendar',
        connect(mapStateToProps, mapDispatchToEvents)(DatepickerCalendar),
    );
